import to from 'await-to-js'
import { TableTypeSlug } from 'models/companies'
import { clientInstance } from 'utils/http-client'
import { notifyError } from './toast'
import { baseAPICall } from './baseAPICall'
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from './queryClient'
import {
  DEAL_TABLE_KEY,
  CONTACTS_TABLE_KEY,
  LODGING_TABLE_KEY,
  LODGING_DOOR_TABLE_KEY,
  CONVENIENCE_TABLE_KEY,
  CONVENIENCE_DOOR_TABLE_KEY,
  UNIFIED_CHAINS_SUMMARY_KEY,
  UNIFIED_DOORS_SUMMARY_KEY,
} from 'constants/tableQueryKeys'
import { Campaign } from 'models/campaign'
import { ChainListResponse } from 'models/chains'
import {
  CHAINS_TABLE_KEY,
  ALL_CAMPAIGNS_TABLE_KEY,
  CHAINS_REPORTS_TABLE_KEY,
  COLLEGE_AND_UNIVERSITY_TABLE_KEY,
  DOORS_TABLE_KEY,
  DOORS_REPORTS_TABLE_KEY,
  getCampaignTableSuffix,
  HOSPITALS_TABLE_KEY,
  NURSING_HOMES_TABLE_KEY,
  K12_TABLE_KEY,
  UGC_TABLE_KEY,
} from 'constants/tableQueryKeys'
import lodash from 'lodash'
import { downloadFile } from 'utils/csvDownloader'
import { getCampaignSourceFilter } from 'features/campaigns/campaignPushTarget'
import { FilterIdentifier, getSavedViewIdentifier } from 'models/saved_view'
import { invalidateSavedFilterQueries } from './saved_filters'
import { adaptUnifiedListParams } from './opportunities/unifiedResponseAdapter'
import {
  tryGetTargetType,
  UnifiedTargetType,
} from './opportunities/targetTypeMap'

interface CommonTableActionParams {
  include_ids: number[]
  exclude_ids: number[]
  filter_params: Record<string, unknown>
}

/**
 * Invalidate the unified opps/doors summary queries that back the chains/doors
 * stat cards. Safe to call unconditionally after a mutation that changes a
 * chain/door's membership or LTV (a no-op when the queries aren't mounted).
 */
const invalidateUnifiedSummaries = (): void => {
  void queryClient.invalidateQueries({
    queryKey: [UNIFIED_CHAINS_SUMMARY_KEY],
    exact: false,
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: [UNIFIED_DOORS_SUMMARY_KEY],
    exact: false,
    refetchType: 'all',
  })
}

/**
 * Get the endpoint for a NON-opportunities table type.
 *
 * Routing note (FIR-6997 → FIR-7878): every opportunities vertical routes its
 * bulk/campaign/export actions through the unified `/opportunities/` endpoints
 * via {@link unifiedActionOverride} (the callers always pass `unifiedTargetType`
 * for those tables), and their explicit-id detail/mobile callers target the
 * vertical-agnostic `contacts/contactcompanies` / `contacts/doorproxies`
 * chokepoints. The per-vertical opps routes were deleted server-side, so this
 * map now holds only the genuinely non-opps surfaces (deals, contacts,
 * companies/UGC, reports, campaigns, activity, rebates, unmatched doors); a
 * stray opps slug falls to the contacts chokepoint default rather than a dead
 * URL. Select-all actions must use the unified engine so the server re-resolves
 * the same rows the user saw (FIR-7822).
 *
 * @param tableTypeSlug - The table type slug
 * @param endpoint - The endpoint
 * @param args - The arguments to be formatted into the full result (path + endpoint). This will only be used if the string contains a % character.
 * @returns The formatted endpoint
 */
function getEndpointForTableType(
  tableTypeSlug: TableTypeSlug,
  endpoint: string,
  ...args: string[]
): string {
  function safeFormat(str: string, ...args: string[]) {
    if (!str.includes('%')) return str
    let i = 0
    return str.replace(/%/g, () => args[i++] ?? '%')
  }

  // FIR-7878: only NON-opportunities tables live here now. Every opportunities
  // vertical (restaurant/lodging/cstore/education/healthcare) routes its bulk +
  // campaign + export actions through `unifiedActionOverride` (which fires
  // whenever `unifiedTargetType` is set — always, for those tables), so this map
  // is only consulted on the non-override fallback path used by the UGC
  // companies table, deals, reports, campaigns, activity, rebates, and unmatched
  // doors. The per-vertical `restaurants/*`, `places/*`, `universities`, `k12`,
  // `hospital`, `nursing_home` list/bulk routes have been deleted server-side —
  // a stray opps caller now falls to the `contacts/contactcompanies` chokepoint
  // default below rather than a dead URL.
  const endpointCompanyTypeMap: Partial<Record<TableTypeSlug, string>> = {
    'contacts-table': 'contacts',
    'deals-table': 'contacts/deals', // Used for list, detail, bulk operations
    'deals-table-reports': 'reports/deals',
    'deals-pipeline-table': 'reports/dashboard/deals-pipeline',
    'companies-table': 'contacts/contactcompanies',
    'campaigns-table': 'contacts/campaigns',
    'activity-table': 'activity/v2/team',
    'activity-my-table': 'activity/v2/my',
    'rebate-claims-table': 'tradespend/firstbite/claims',
    'rebate-offers-table': 'tradespend/firstbite/offers',
    'unmatched-doors-table': 'contacts/doorproxies',
    'doors-reports-table': 'reports/doors',
    'chains-reports-table': 'reports/chains',
  }

  const prefix =
    endpointCompanyTypeMap[tableTypeSlug] ?? 'contacts/contactcompanies'
  const url = `${prefix}${endpoint}`
  if (args.length > 0) {
    return safeFormat(url, ...args)
  }
  return url
}

/**
 * Invalidate every query scoped to a campaign after its target membership
 * changes (bulk assign/unassign, contact import).
 *
 * Covers both key families: campaign-scoped tables, whose first key element
 * ends with `getCampaignTableSuffix(campaignId)` (targets table for every
 * company type plus campaign contacts/deals), and `['campaign', <id>, ...]`
 * metadata queries (tab counts, zero-state gates, contact requests, lead
 * review). Must stay exhaustive: refetchOnMount is disabled globally, so any
 * missed query serves stale data until a hard refresh (FIR-7460).
 * `refetchType: 'all'` because these queries usually live on a different page
 * than the table the action ran on.
 */
export function invalidateCampaignTargetQueries(campaignId: number) {
  const campaignTableSuffix = getCampaignTableSuffix(campaignId)
  void queryClient.invalidateQueries({
    predicate: (query) =>
      typeof query.queryKey[0] === 'string' &&
      query.queryKey[0].endsWith(campaignTableSuffix),
    refetchType: 'all',
  })
  void queryClient.invalidateQueries({
    queryKey: ['campaign', campaignId],
    exact: false,
    refetchType: 'all',
  })
}

/* -------------
Assign To Campaign
-------------- */
// Refer to server/common/table_actions_mixins/BulkAssignToCampaignMixin.py for the server side implementation

/**
 * Build the unified `/opportunities/` endpoint + adapted query params for a bulk
 * action, or return `null` when not routing to unified (caller keeps legacy).
 *
 * Under the unified-opps flag the targets tables render rows from
 * `/opportunities/` (chains) / `/opportunities/doors/` (locations), so a
 * *select-all* action (empty `include_ids`) must be re-resolved by that SAME
 * engine — otherwise the server re-resolves the filters through the legacy
 * engine and the action hits the wrong (tiny) set (the FIR-7822 prod bug). When
 * `unifiedTargetType` is set we point the action at the unified endpoint and
 * send the list's adapted params (incl. `target_type`) so the resolved set
 * matches the rows the user saw. `unifiedDoors` selects the doors-scoped
 * endpoint variant. `unifiedSuffix` is the path after the surface prefix, e.g.
 * `campaigns/assign/` or `bulk/tags/`.
 */
export function unifiedActionOverride(
  unifiedSuffix: string,
  filterParams: Record<string, unknown>,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
): { url: string; params: Record<string, unknown> } | null {
  if (!unifiedTargetType) {
    return null
  }
  const base = unifiedDoors ? 'opportunities/doors/' : 'opportunities/'
  return {
    url: `${base}${unifiedSuffix}`,
    params: {
      ...adaptUnifiedListParams(filterParams, unifiedTargetType),
      target_type: unifiedTargetType,
    },
  }
}

/**
 * Resolve the endpoint + query params for a campaign assign/unassign call,
 * routing to the unified endpoint when `unifiedTargetType` is set (see
 * {@link unifiedActionOverride}), else the legacy per-vertical endpoint.
 */
function resolveCampaignActionRequest(
  companyType: TableTypeSlug,
  filterParams: Record<string, unknown>,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
): { url: string; params: Record<string, unknown> } {
  return (
    unifiedActionOverride(
      'campaigns/assign/',
      filterParams,
      unifiedTargetType,
      unifiedDoors
    ) ?? {
      url: getEndpointForTableType(companyType, '/campaigns/assign/'),
      params: filterParams,
    }
  )
}

/**
 * Resolve the endpoint + query params for a distributors/gpos membership action,
 * shared by {@link commonManageDistributors} and {@link commonManageGPOs}.
 *
 * Routes to the unified endpoint when `unifiedTargetType` is set (see
 * {@link unifiedActionOverride}), else the per-vertical `legacyUrl`. The UGC
 * companies table (never unified) carries its `sub_category_id` as a query param.
 */
function resolveMembershipActionRequest(
  unifiedSuffix: string,
  legacyUrl: string,
  filterParams: Record<string, unknown>,
  routing: { unifiedTargetType?: UnifiedTargetType; unifiedDoors?: boolean },
  companies: { isCompaniesTable: boolean; subCategoryId?: number }
): { url: string; params: Record<string, unknown> } {
  const override = unifiedActionOverride(
    unifiedSuffix,
    filterParams,
    routing.unifiedTargetType,
    routing.unifiedDoors
  )
  const url = override?.url ?? legacyUrl
  const params = override?.params ?? filterParams
  if (companies.isCompaniesTable && !override) {
    params['sub_category_id'] = companies.subCategoryId
  }
  return { url, params }
}

type AssignToCampaignRequest = {
  campaign_id: number
  campaign_name: string
  campaign_color: string
  target_type_slug?: string
  clear_type?: boolean
} & CommonTableActionParams

async function assignToCampaign(
  companyType: TableTypeSlug,
  body: AssignToCampaignRequest,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean,
  filterIdentifier?: FilterIdentifier
) {
  const { url, params } = resolveCampaignActionRequest(
    companyType,
    body.filter_params,
    unifiedTargetType,
    unifiedDoors
  )
  return baseAPICall(
    () =>
      clientInstance.post(
        url,
        {
          campaign_id: body.campaign_id,
          include_ids: body.include_ids,
          exclude_ids: body.exclude_ids,
          assign: true,
          target_type_slug: body.target_type_slug ?? '',
          clear_type: body.clear_type ?? false,
          // Snapshot of the pushing table's applied filters, taken at request
          // time; the backend auto-saves it as a SavedView linked to the
          // campaign (FIR-7843). The helper only returns fields on a
          // SELECT-ALL push (empty include_ids) with filters applied — the
          // one case where the filter genuinely defines the pushed set —
          // and spreads to nothing otherwise.
          ...getCampaignSourceFilter(filterIdentifier, body.include_ids),
        },
        {
          params,
        }
      ),
    {
      successMessage: 'Succesfully assigned to campaign',
    }
  )
}

export const useAssignToCampaignMutation = (
  oppsListQueryKey: string[],
  chainProxyIdAccessor = 'id',
  companyTypeSlug: TableTypeSlug,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean,
  /** FilterStore identifier of the pushing table (incl. any modifier suffix).
   *  When set, a select-all push auto-saves the table's applied filters as
   *  the campaign's source filter (FIR-7843); subset selections never do. */
  filterIdentifier?: FilterIdentifier
) => {
  return useMutation({
    mutationFn: (variables: AssignToCampaignRequest) =>
      assignToCampaign(
        companyTypeSlug,
        variables,
        unifiedTargetType,
        unifiedDoors,
        filterIdentifier
      ),
    onMutate: async (newCampaign) => {
      if (!oppsListQueryKey) {
        return
      }

      // Base table
      await queryClient.cancelQueries({ queryKey: oppsListQueryKey })
      const prev = queryClient.getQueryData(oppsListQueryKey) as {
        results: any[]
      }
      queryClient.setQueryData(oppsListQueryKey, (old: ChainListResponse) => {
        const data = structuredClone(old)

        newCampaign.include_ids?.forEach((id) => {
          const row = data.results.find(
            (d) => lodash.get(d, chainProxyIdAccessor) === id
          )
          if (row) {
            if (
              !row.campaigns?.some(
                (cmp) => cmp.name === newCampaign.campaign_name
              )
            ) {
              row.campaigns = [
                ...(row.campaigns || []),
                {
                  id: newCampaign.campaign_id,
                  name: newCampaign.campaign_name,
                  color: newCampaign.campaign_color,
                } as Campaign,
              ]
            }
          }
        })

        return data
      })

      // No optimistic update for the campaign-scoped tables: they are never
      // rendered on the page the assign action runs from, and the campaign
      // table key prefix also matches sibling queries with different shapes
      // (e.g. `[..., 'summary']`), which a blind results-push corrupted and
      // could even crash, aborting the mutation before the request was sent
      // (FIR-7460). onSettled invalidates them instead.

      return { prev }
    },
    onError: (_, __, context) => {
      // Restore base table
      queryClient.setQueryData(oppsListQueryKey, context?.prev)
    },
    onSettled: async (_, __, variables) => {
      // note: the reason we invalidate the query rather than use optimistic ui
      // is because if we remove all rows in the current page, we need to
      // fetch from the server

      // Invalidate every campaign-scoped query (targets/contacts/deals tables,
      // tab counts, zero-state gates); see invalidateCampaignTargetQueries
      invalidateCampaignTargetQueries(variables.campaign_id)

      // Stat cards + select-all totals read the unified summaries.
      invalidateUnifiedSummaries()

      // Invalidate campaigns list: type may have changed via inference
      void queryClient.invalidateQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
        exact: false,
        refetchType: 'all',
      })

      // The push may have auto-saved the table's filter as a SavedView
      // (FIR-7843), so refresh the saved-filter dropdown + settings caches.
      if (filterIdentifier) {
        void invalidateSavedFilterQueries(
          getSavedViewIdentifier(filterIdentifier)
        )
      }
    },
  })
}

/* -------------
Unassign From Campaign
-------------- */
type UnassignFromCampaignRequest = {
  campaign_id: number
} & CommonTableActionParams

async function unassignFromCampaign(
  companyType: TableTypeSlug,
  body: UnassignFromCampaignRequest,
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
) {
  const { url, params } = resolveCampaignActionRequest(
    companyType,
    body.filter_params,
    unifiedTargetType,
    unifiedDoors
  )
  return baseAPICall(
    () =>
      clientInstance.post(
        url,
        {
          campaign_id: body.campaign_id,
          include_ids: body.include_ids,
          exclude_ids: body.exclude_ids,
          assign: false,
        },
        {
          params,
        }
      ),
    {
      successMessage: 'Succesfully unassigned from campaign',
    }
  )
}

export const useUnassignToCampaignMutation = (
  companyType: TableTypeSlug,
  oppsListQueryKey: string[],
  chainProxyIdAccessor = 'id',
  unifiedTargetType?: UnifiedTargetType,
  unifiedDoors?: boolean
) => {
  return useMutation({
    mutationFn: (variables: UnassignFromCampaignRequest) =>
      unassignFromCampaign(
        companyType,
        variables,
        unifiedTargetType,
        unifiedDoors
      ),
    onMutate: async (newCampaign) => {
      if (!oppsListQueryKey) {
        return
      }
      await queryClient.cancelQueries({ queryKey: oppsListQueryKey })

      const prev = queryClient.getQueryData(oppsListQueryKey)

      queryClient.setQueryData(oppsListQueryKey, (old: any) => {
        const data = structuredClone(old)

        if (
          newCampaign.include_ids.length === 0 &&
          newCampaign.exclude_ids.length === 0
        ) {
          data.results = []
          return data
        }

        const results = data.results.filter(
          (it: any) =>
            !newCampaign.include_ids?.includes(
              lodash.get(it, chainProxyIdAccessor)
            )
        )

        data.results = results

        return data
      })

      return { prev }
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(oppsListQueryKey, context?.prev)
    },
    onSettled: async (_, __, variables) => {
      // note: the reason we invalidate the query rather than use optimistic ui
      // is because if we remove all rows in the current page, we need to
      // fetch from the server

      // Invalidate the table the action ran on (the campaign targets table;
      // unassign is only offered there)
      void queryClient.invalidateQueries({
        queryKey: oppsListQueryKey,
        exact: false,
      })

      // Invalidate every campaign-scoped query (targets/contacts/deals tables,
      // tab counts, zero-state gates); see invalidateCampaignTargetQueries
      invalidateCampaignTargetQueries(variables.campaign_id)

      // Stat cards + select-all totals read the unified summaries.
      invalidateUnifiedSummaries()

      // Invalidate campaigns list: mutation symmetry with assign
      void queryClient.invalidateQueries({
        queryKey: [ALL_CAMPAIGNS_TABLE_KEY],
        exact: false,
        refetchType: 'all',
      })
    },
  })
}

/* -------------
Create Deals
-------------- */
// Refer to server/common/table_actions_mixins/BulkCreateDealsMixin.py for the server side implementation
export async function commonCreateDeals(
  slug: TableTypeSlug,
  options: {
    deals: {
      sales_stage_id?: number | null
      account_owner_id?: number | null
      product_id?: number
      product_variant_id?: number | null
      origin_campaign_id?: number | null
      monthly_volume_override?: number | null
      monthly_revenue_override?: number | null
      close_date_override?: string | null
      name?: string | null
    }[]
    apply_to?: string
    door_ids?: number[]
    door_proxy_id?: number
    unifiedTargetType?: UnifiedTargetType
    unifiedDoors?: boolean
  } & CommonTableActionParams
) {
  // Routing params are not request data — keep them out of the body so they
  // can never leak onto the wire (the body below is already explicit; this also
  // future-proofs against a refactor that spreads `options`).
  const { unifiedTargetType, unifiedDoors, ...dealOptions } = options
  const override = unifiedActionOverride(
    'bulk/deals/',
    dealOptions.filter_params,
    unifiedTargetType,
    unifiedDoors
  )
  const url = override?.url ?? getEndpointForTableType(slug, '/bulk/deals/')

  const [err, res] = await to(
    clientInstance.post(
      url,
      {
        include_ids: dealOptions.include_ids,
        exclude_ids: dealOptions.exclude_ids,
        deals: dealOptions.deals,
        apply_to: dealOptions.apply_to,
        door_ids: dealOptions.door_ids,
        door_proxy_id: dealOptions.door_proxy_id,
      },
      {
        params: override?.params ?? dealOptions.filter_params,
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  return res
}

/**
 * Suffix of the LIVE query key for the "Custom Locations" (unmatched doors)
 * table under each vertical. Built in `UnmatchedChainDoorTable.tsx` as
 * `${tableKey}_DOORS` from the `tableKey` passed by `opportunities.tsx`
 * (`${subCategoryActive.slug}-unmatched-table`) — one concrete key per
 * `OpportunitySubCategorySlug`, not a fixed string `TABLE_INVALIDATION_KEYS`
 * can hold. Matched via suffix predicate instead (FIR-7845), mirroring
 * {@link invalidateCampaignTargetQueries} below. Does NOT match the
 * `unmatched-doors-table` tables rendered from `UnmatchedDoorsModal` or
 * `DoorsTableTabs` (keyed `...-unmatched-modal-<id>` / `<prefix>-<id>`,
 * per-chain-proxy rather than per-vertical) — those are a separate,
 * pre-existing staleness gap, not covered by this fix.
 */
const UNMATCHED_DOORS_TABLE_KEY_SUFFIX = '-unmatched-table_DOORS'

/**
 * Table-query invalidation map shared by {@link commonManageTags} and
 * {@link commonSetHidden} (FIR-7845) — both mutate chain/door membership on
 * the same tables, so they invalidate the same caches. NOTE:
 * {@link commonManageDistributors} keeps its own copy — its keys (`doors`,
 * `chains`, `universities`, …) and match mode (`exact` default) differ from
 * this one, so it isn't DRY-able into this const without changing behavior.
 */
const TABLE_INVALIDATION_KEYS: Record<string, string[]> = {
  // Summary/stat-card invalidation note: since FIR-7869 the chains/doors stat
  // cards read ONLY the unified summary keys (UNIFIED_CHAINS_SUMMARY_KEY /
  // UNIFIED_DOORS_SUMMARY_KEY — the legacy chains_summary/places_summary
  // queries are gone), and {@link commonSetHidden} refreshes those via
  // {@link invalidateUnifiedSummaries}, so this map carries table keys only.
  'restaurants-bars': [CHAINS_TABLE_KEY, DOORS_TABLE_KEY],
  'education-cu': [COLLEGE_AND_UNIVERSITY_TABLE_KEY],
  'education-k-12': [K12_TABLE_KEY],
  'healthcare-hospitals': [HOSPITALS_TABLE_KEY],
  'healthcare-nursinghomes': [NURSING_HOMES_TABLE_KEY],

  companies: [UGC_TABLE_KEY],
  companies_from_contacts: [CONTACTS_TABLE_KEY],
  'contacts-table': [CONTACTS_TABLE_KEY],
  deals: [DEAL_TABLE_KEY],
  'lodging-hospitality': [LODGING_TABLE_KEY],
  'lodging-hospitality-doors': [LODGING_DOOR_TABLE_KEY],
  convenience: [CONVENIENCE_TABLE_KEY],
  'convenience-doors': [CONVENIENCE_DOOR_TABLE_KEY],
  'unmatched-doors-table': [DOORS_TABLE_KEY, UGC_TABLE_KEY],
  // FIR-7845 review (finding 1): 'doors-reports-table'/'chains-reports-table'
  // are TableTypeSlug values, not live query keys — no query is ever keyed by
  // them. The doors/chains tables always render with tableKey=DOORS_TABLE_KEY
  // / CHAINS_TABLE_KEY (see pages/app/opportunities/opportunities.tsx)
  // regardless of legacy-vs-reports variant. Keep the slug-named keys too —
  // harmless over-invalidation, cheap insurance if something is ever actually
  // keyed by the slug.
  'doors-reports-table': [DOORS_REPORTS_TABLE_KEY, DOORS_TABLE_KEY],
  'chains-reports-table': [CHAINS_REPORTS_TABLE_KEY, CHAINS_TABLE_KEY],
}

/**
 * Invalidate every query cache scoped to `tableType`, for the mutations that
 * share {@link TABLE_INVALIDATION_KEYS} ({@link commonManageTags},
 * {@link commonSetHidden} — FIR-7845). Covers the static keys in the map,
 * plus, for `unmatched-doors-table`, a suffix-predicate sweep over the
 * per-vertical dynamic keys the map can't express (see
 * {@link UNMATCHED_DOORS_TABLE_KEY_SUFFIX}).
 */
function invalidateTableTypeQueries(tableType: TableTypeSlug): void {
  TABLE_INVALIDATION_KEYS[tableType]?.forEach((key) => {
    void queryClient.invalidateQueries({ queryKey: [key], exact: false })
  })

  if (tableType === 'unmatched-doors-table') {
    void queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === 'string' &&
        query.queryKey[0].endsWith(UNMATCHED_DOORS_TABLE_KEY_SUFFIX),
    })
  }
}

/* -------------
Manage Tags
-------------- */
export type ManageTagsDistributorsParams = {
  items: number[]
  action: 'add' | 'delete'
  door_proxy_ids?: number[]
} & CommonTableActionParams

export async function commonManageTags({
  values,
  tableType: companyType,
  isContactTags = false,
  unifiedTargetType,
  unifiedDoors,
}: {
  values: ManageTagsDistributorsParams
  tableType: TableTypeSlug
  isContactTags?: boolean
  unifiedTargetType?: UnifiedTargetType
  unifiedDoors?: boolean
}) {
  const { filter_params, ...rest } = values

  // Contact tags are not a unified-opps action — only the chain/door tag
  // endpoints route to unified.
  const override = isContactTags
    ? null
    : unifiedActionOverride(
        'bulk/tags/',
        filter_params ?? {},
        unifiedTargetType,
        unifiedDoors
      )
  const endpoint = isContactTags ? '/bulk/contacttags/' : '/bulk/tags/'
  const url = override?.url ?? getEndpointForTableType(companyType, endpoint)

  const queryParams = override?.params ?? filter_params ?? {}

  // add company_type_slug to the request

  const [err, res] = await to(
    clientInstance.post(
      url,
      { ...rest },
      {
        params: queryParams,
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  // Invalidate corresponding queries (FIR-7845: shared with commonSetHidden)
  invalidateTableTypeQueries(companyType)

  return res
}

/* -------------
Hide / Unhide records (FIR-7845)
-------------- */
/**
 * Set the per-instance is_hidden flag on chain or door proxies. Explicit ids
 * only — row ids are proxy ids under BOTH the legacy and unified tables, so
 * one endpoint pair serves every table and select-all re-resolution (the
 * FIR-7822 trap) never applies. Hide and unhide invalidate the same caches.
 */
export async function commonSetHidden({
  contactCompanyIds,
  doorProxyIds,
  hidden,
  tableType,
}: {
  contactCompanyIds?: number[]
  doorProxyIds?: number[]
  hidden: boolean
  tableType: TableTypeSlug
}) {
  const isDoors = !!doorProxyIds?.length
  const url = isDoors
    ? 'contacts/doorproxies/bulk/hide/'
    : 'contacts/contactcompanies/bulk/hide/'
  const body = isDoors
    ? { door_proxy_ids: doorProxyIds, hidden }
    : { contact_company_ids: contactCompanyIds, hidden }

  const [err, res] = await to(clientInstance.post(url, body))
  if (err) {
    notifyError(err.message)
    throw err
  }

  invalidateTableTypeQueries(tableType)

  // Flag-on stat cards read the unified summaries instead of the legacy
  // keys, so refresh those too (no-op when the flag is off / not mounted).
  invalidateUnifiedSummaries()

  return res
}

/* -------------
Manage Deal Tags
-------------- */
export async function commonManageDealTags({
  values,
}: {
  values: ManageTagsDistributorsParams
}) {
  const url = 'contacts/deals/bulk/deal-tags/'

  const { filter_params, ...rest } = values

  const queryParams = filter_params ?? {}

  const [err, res] = await to(
    clientInstance.post(
      url,
      { ...rest },
      {
        params: queryParams,
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  queryClient.invalidateQueries({ queryKey: [DEAL_TABLE_KEY], exact: false })

  return res
}

/* -------------
Manage Distributors
-------------- */
export async function commonManageDistributors({
  tableType: type,
  values,
  subCategoryId,
  unifiedTargetType,
  unifiedDoors,
}: {
  tableType: TableTypeSlug
  values: ManageTagsDistributorsParams
  subCategoryId?: number
  unifiedTargetType?: UnifiedTargetType
  unifiedDoors?: boolean
}) {
  // FIR-7878: opp-type verticals route through unifiedActionOverride (tables
  // pass unifiedTargetType) or the generic contacts chokepoint (explicit-id
  // detail/mobile). The per-vertical restaurant entries pointed at now-deleted
  // routes, so they were removed; a stray call falls to the companies-table
  // chokepoint default below. Reports + non-opp tables keep their own routes.
  const urlMap: Partial<Record<TableTypeSlug, string>> = {
    'deals-table': 'contacts/deals/bulk/distributors/',
    'deals-table-reports': 'reports/deals/bulk/distributors/',
    'companies-table': `contacts/contactcompanies/bulk/distributors/`,
    'unmatched-doors-table': 'contacts/doorproxies/bulk/distributors/',
    'doors-reports-table': 'reports/doors/bulk/distributors/',
    'chains-reports-table': 'reports/chains/bulk/distributors/',
  }

  const { filter_params, ...rest } = values
  const { url, params: queryParams } = resolveMembershipActionRequest(
    'bulk/distributors/',
    urlMap[type] ?? (urlMap['companies-table'] as string),
    filter_params ?? {},
    { unifiedTargetType, unifiedDoors },
    { isCompaniesTable: type === 'companies-table', subCategoryId }
  )

  const [err, res] = await to(
    clientInstance.post(url, rest, {
      params: queryParams,
    })
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  // Invalidate corresponding queries
  const queryKeysMap: Record<string, string[]> = {
    doors: [CHAINS_TABLE_KEY, DOORS_TABLE_KEY],
    chains: [CHAINS_TABLE_KEY, DOORS_TABLE_KEY],
    companies: [UGC_TABLE_KEY],
    deals: [DEAL_TABLE_KEY],
    universities: [COLLEGE_AND_UNIVERSITY_TABLE_KEY],
    k12: [K12_TABLE_KEY],
    hospitals: [HOSPITALS_TABLE_KEY],
    nursing_homes: [NURSING_HOMES_TABLE_KEY],
    lodging: [LODGING_TABLE_KEY],
    'unmatched-doors-table': [DOORS_TABLE_KEY, UGC_TABLE_KEY],
    'doors-reports-table': [DOORS_REPORTS_TABLE_KEY],
    'chains-reports-table': [CHAINS_REPORTS_TABLE_KEY],
  }
  if (type in queryKeysMap) {
    queryKeysMap[type].forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] })
    })
  }

  return res
}

/* -------------
Manage GPOs
-------------- */
export async function commonManageGPOs({
  tableType: type,
  values,
  subCategoryId,
  unifiedTargetType,
  unifiedDoors,
}: {
  tableType: TableTypeSlug
  values: ManageTagsDistributorsParams
  subCategoryId?: number
  unifiedTargetType?: UnifiedTargetType
  unifiedDoors?: boolean
}) {
  // FIR-7878: restaurant per-vertical entries removed (deleted routes); stray
  // calls fall to the companies-table chokepoint default. See the distributors
  // urlMap above.
  const urlMap: Partial<Record<TableTypeSlug, string>> = {
    'deals-table': 'contacts/deals/bulk/gpos/',
    'deals-table-reports': 'reports/deals/bulk/gpos/',
    'companies-table': `contacts/contactcompanies/bulk/gpos/`,
    'unmatched-doors-table': 'contacts/doorproxies/bulk/gpos/',
    'doors-reports-table': 'reports/doors/bulk/gpos/',
    'chains-reports-table': 'reports/chains/bulk/gpos/',
  }

  const { filter_params, ...rest } = values
  const { url, params: queryParams } = resolveMembershipActionRequest(
    'bulk/gpos/',
    urlMap[type] ?? (urlMap['companies-table'] as string),
    filter_params ?? {},
    { unifiedTargetType, unifiedDoors },
    { isCompaniesTable: type === 'companies-table', subCategoryId }
  )

  const [err, res] = await to(
    clientInstance.post(url, rest, {
      params: queryParams,
    })
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  return res
}

/* -------------
Export Table
-------------- */
export type CommonExportTableOptions = {
  include_ids: number[]
  exclude_ids: number[]
  filter_params: Record<string, unknown>
  selected_columns: string[]
  export_type: 'csv' | 'xlsx'
}

function getDataTypeNameForTableType(type: TableTypeSlug) {
  const dataTypeNameMap: Partial<Record<TableTypeSlug, string>> = {
    'lodging-hospitality': 'Lodging & Hospitality',
    'lodging-hospitality-doors': 'Lodging & Hospitality Doors',
    'healthcare-hospitals': 'Hospitals',
    'healthcare-nursinghomes': 'Senior Living',
    'education-cu': 'Universities',
    'education-k-12': 'K-12 Districts',
    'deals-pipeline-table': 'Deals Pipeline',
    'campaigns-table': 'Campaigns',
    'activity-table': 'Activity',
    'restaurants-bars-doors': 'Restaurants & Bars Doors',
    'restaurants-bars': 'Restaurants & Bars',
    'deals-table': 'Deals',
    'deals-table-reports': 'Deals',
    'companies-table': 'Companies',
    'contacts-table': 'Contacts',
    'rebate-claims-table': 'Rebate Claims',
    'rebate-offers-table': 'Rebate Offers',
    convenience: 'Convenience Stores',
    'convenience-doors': 'Convenience Stores -  Doors',
    'doors-reports-table': 'Doors CRM Reports',
    'chains-reports-table': 'Chains CRM Reports',
  }
  return dataTypeNameMap[type] ?? 'Unknown'
}

export async function commonExportTable(
  type: TableTypeSlug,
  options: CommonExportTableOptions,
  abortController?: AbortController,
  overrideDataTypeName?: string,
  endpointArgs?: string[]
) {
  const url = getEndpointForTableType(
    type,
    '/export/',
    ...(endpointArgs ? endpointArgs : [])
  )

  const [err, res] = await to(
    clientInstance.post(
      url,
      {
        selected_columns: options.selected_columns,
        export_type: options.export_type,
        include_ids: options.include_ids,
        exclude_ids: options.exclude_ids,
      },
      {
        params: options.filter_params,
        signal: abortController?.signal,
        responseType: 'blob',
      }
    )
  )

  if (err) {
    notifyError(err.message)
    throw err
  }

  downloadFile(
    res.data,
    `First Bite Export - ${overrideDataTypeName ?? getDataTypeNameForTableType(type)} - ${new Date().toLocaleDateString()}.${options.export_type}`
  )
}

/* -------------
GET City States
-------------- */
// FIR-7878: the curated City & State lookup moved off the per-vertical
// google_places routes onto the unified `opportunities/citystates/` endpoint,
// keyed by target_type. Only lodging + cstore have a curated table; doors reuse
// their chain's lookup, so strip a trailing `-doors` before resolving.
function citystatesTargetType(
  companyType: TableTypeSlug
): UnifiedTargetType | undefined {
  return tryGetTargetType(companyType.replace(/-doors$/, ''))
}

async function commonGetCityStates(companyType: TableTypeSlug) {
  return baseAPICall<{ value: string; label: string }[]>(() =>
    clientInstance.get('opportunities/citystates/', {
      params: { target_type: citystatesTargetType(companyType) },
    })
  )
}

export const useCommonGetCityStates = (companyType: TableTypeSlug) => {
  return useQuery({
    queryKey: ['citystates', companyType],
    queryFn: () => commonGetCityStates(companyType),
  })
}

/* -------------
GET City States - Paginated
-------------- */
export interface CityStatesParams {
  search?: string
  state?: string // Comma-separated state codes
  page_size?: number
}

export interface CityStatesPaginatedResponse {
  results: { value: string; label: string }[]
  count: number
  next: string | null
  previous: string | null
}

async function commonGetCityStatesPaginated(
  companyType: TableTypeSlug,
  params: CityStatesParams & { page?: number }
) {
  return baseAPICall<CityStatesPaginatedResponse>(() =>
    clientInstance.get('opportunities/citystates/', {
      params: { ...params, target_type: citystatesTargetType(companyType) },
    })
  )
}

export const useCommonGetCityStatesInfinite = (
  companyType: TableTypeSlug,
  params: CityStatesParams = {},
  enabled = true
) => {
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['citystates-infinite', companyType, params],
    queryFn: ({ pageParam }) =>
      commonGetCityStatesPaginated(companyType, { ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined
      const pageStr = new URL(lastPage.next).searchParams.get('page')
      return pageStr ? parseInt(pageStr, 10) : undefined
    },
    enabled,
  })
}
